import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { MessageCircle, Shield, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=phone, 2=code+newPassword
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [devCode, setDevCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError("");
    if (!phone.trim()) {
      setError("الرجاء إدخال رقم الموبايل");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { phone });
      toast.success(data.message);
      // DEV mode: show the code on screen (would come via WhatsApp in production)
      if (data.dev_code) {
        setDevCode(data.dev_code);
      }
      setStep(2);
    } catch (err) {
      const msg = err.response?.data?.detail || "حدث خطأ، حاول مرة أخرى";
      setError(typeof msg === "string" ? msg : "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("كلمة السر غير متطابقة");
      return;
    }
    if (newPassword.length < 6) {
      setError("كلمة السر يجب أن تكون 6 أحرف على الأقل");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        phone,
        code,
        new_password: newPassword,
      });
      toast.success("تم تغيير كلمة السر بنجاح");
      navigate("/login");
    } catch (err) {
      const msg = err.response?.data?.detail || "حدث خطأ، حاول مرة أخرى";
      setError(typeof msg === "string" ? msg : "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-heading font-bold text-4xl text-[#2D4A3E]">جملتي</h1>
          </Link>
          <p className="text-[#5C6E64] mt-2">
            {step === 1 ? "نسيت كلمة السر؟" : "أدخل رمز التحقق"}
          </p>
        </div>

        {step === 1 ? (
          <form
            onSubmit={handleRequestCode}
            className="space-y-4 bg-[#FAFCFA] border border-[#E4EBE6] rounded-3xl p-6 sm:p-8"
          >
            <div className="flex items-center justify-center gap-2 p-4 bg-[#E85D45]/10 rounded-2xl mb-2">
              <MessageCircle className="w-5 h-5 text-[#E85D45]" />
              <p className="text-sm text-[#2D4A3E]">
                سنرسل لك رمز التحقق عبر واتساب
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">
                رقم الموبايل المسجل
              </label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="forgot-phone"
                className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
                placeholder="07XXXXXXXXX"
                required
              />
            </div>

            {error && (
              <p data-testid="forgot-error" className="text-[#E85D45] text-sm">
                {error}
              </p>
            )}

            <motion.button
              type="submit"
              disabled={loading}
              whileTap={{ scale: 0.98 }}
              data-testid="forgot-submit"
              className="w-full bg-[#2D4A3E] hover:bg-[#233A30] text-white font-bold py-3 rounded-full transition-colors disabled:opacity-50"
            >
              {loading ? "جاري الإرسال..." : "إرسال رمز التحقق"}
            </motion.button>

            <Link
              to="/login"
              data-testid="back-to-login"
              className="block text-center text-sm text-[#5C6E64] hover:text-[#2D4A3E] hover:underline"
            >
              العودة إلى تسجيل الدخول
            </Link>
          </form>
        ) : (
          <form
            onSubmit={handleResetPassword}
            className="space-y-4 bg-[#FAFCFA] border border-[#E4EBE6] rounded-3xl p-6 sm:p-8"
          >
            <div className="flex items-center justify-center gap-2 p-4 bg-[#7ED321]/10 rounded-2xl mb-2">
              <CheckCircle2 className="w-5 h-5 text-[#7ED321]" />
              <p className="text-sm text-[#2D4A3E]">
                تم إرسال الرمز إلى {phone}
              </p>
            </div>

            {/* DEV mode banner showing the code */}
            {devCode && (
              <div
                data-testid="dev-code-banner"
                className="p-4 bg-[#F5A623]/10 border-2 border-dashed border-[#F5A623] rounded-2xl text-center"
              >
                <p className="text-xs text-[#5C6E64] mb-1">
                  وضع التطوير - رمز التحقق:
                </p>
                <p className="font-mono font-bold text-2xl text-[#E85D45] tracking-widest">
                  {devCode}
                </p>
                <p className="text-xs text-[#5C6E64] mt-1">
                  (سيتم إرساله عبر واتساب في الإنتاج)
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">
                رمز التحقق (6 أرقام)
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="reset-code"
                className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white text-center text-2xl font-mono tracking-widest"
                placeholder="------"
                maxLength={6}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">
                كلمة السر الجديدة
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="reset-new-password"
                className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
                placeholder="6 أحرف على الأقل"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#111814] mb-2">
                تأكيد كلمة السر
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="reset-confirm-password"
                className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E] bg-white"
                placeholder="أعد كتابة كلمة السر"
                required
              />
            </div>

            {error && (
              <p data-testid="reset-error" className="text-[#E85D45] text-sm">
                {error}
              </p>
            )}

            <motion.button
              type="submit"
              disabled={loading}
              whileTap={{ scale: 0.98 }}
              data-testid="reset-submit"
              className="w-full bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold py-3 rounded-full transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Shield className="w-4 h-4" />
              {loading ? "جاري التحديث..." : "تغيير كلمة السر"}
            </motion.button>

            <button
              type="button"
              onClick={() => {
                setStep(1);
                setDevCode("");
                setCode("");
              }}
              data-testid="resend-code"
              className="block w-full text-center text-sm text-[#5C6E64] hover:text-[#2D4A3E] hover:underline"
            >
              لم تستلم الرمز؟ إعادة الإرسال
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
